/**
 * Indicadores comerciais do pacote 2 — fórmulas APROVADAS em 11/10/2026 (D1 e D2). Funções puras, usadas pelas telas
 * (Kanban, Lista, Cliente) e pelos testes; nenhuma regra de negócio é alterada aqui, só a leitura dos dados existentes.
 *
 * D1 — Oportunidades:
 *  - Abertas: oportunidades atualmente abertas CRIADAS no período; sem período, todas as abertas. Valor = versão vigente.
 *  - Ganhas e perdidas: pela DATA DE ENCERRAMENTO no período; sem período, histórico completo.
 *  - Ganha = versão aceita (vigente); Ganha parcialmente = só o valor ganho (Pedido gerado).
 *  - Perdido = versão vigente das Perdidas + parte não ganha das parciais. Canceladas ficam fora.
 *  - Conversão em valor = ganho ÷ (ganho + perdido); em quantidade = ganhas ÷ (ganhas + perdidas). Sem denominador: null ("—").
 *  - Cada oportunidade conta uma vez.
 * D2 — Cabeçalho do Cliente (cliente inteiro, histórico completo, independente dos filtros das abas):
 *  - Total: quantidade com todos os status; valor complementar "em abertas + ganhas".
 *  - Pedidos gerados e Ticket médio: Pedidos não cancelados, descontando saldos cancelados;
 *    Ticket médio = valor desses Pedidos ÷ quantidade desses Pedidos.
 */

export type EstagioIndicador = 'prospeccao' | 'qualificacao' | 'proposta_em_elaboracao' | 'proposta_enviada' | 'negociacao' | 'ganha' | 'ganha_parcialmente' | 'perdida' | 'cancelada';

export interface OportunidadeIndicador {
  estagio: EstagioIndicador;
  data_criacao: string;
  data_encerramento: string | null;
  valor_vigente: number | null;
  /** valor do Pedido gerado (ganho parcial: só as quantidades ganhas) */
  valor_ganho?: number | null;
}

export interface PedidoIndicador {
  situacao: string;
  valores: { vendido: number; saldoCancelado?: number };
}

const ENCERRADOS: EstagioIndicador[] = ['ganha', 'ganha_parcialmente', 'perdida', 'cancelada'];
export const estaAberta = (o: Pick<OportunidadeIndicador, 'estagio'>) => !ENCERRADOS.includes(o.estagio);
const arred = (v: number) => Math.round(v * 100) / 100;

/** Valor GANHO de uma oportunidade: Ganha = versão aceita; Ganha parcialmente = valor do Pedido (só o que foi ganho). */
export function valorGanho(o: OportunidadeIndicador): number {
  if (o.estagio === 'ganha') return o.valor_vigente ?? o.valor_ganho ?? 0;
  if (o.estagio === 'ganha_parcialmente') return o.valor_ganho ?? o.valor_vigente ?? 0;
  return 0;
}

/** Valor PERDIDO: versão vigente das Perdidas; parte não ganha das parciais; demais = 0. */
export function valorPerdido(o: OportunidadeIndicador): number {
  if (o.estagio === 'perdida') return o.valor_vigente ?? 0;
  if (o.estagio === 'ganha_parcialmente') return Math.max(0, (o.valor_vigente ?? 0) - valorGanho(o));
  return 0;
}

/** Valor de referência para somar uma seleção mista de status (faixa da aba Oportunidades do Cliente). */
export function valorReferencia(o: OportunidadeIndicador): number {
  return o.estagio === 'ganha' || o.estagio === 'ganha_parcialmente' ? valorGanho(o) : (o.valor_vigente ?? 0);
}

export interface IndicadoresOportunidades {
  abertas: { quantidade: number; valor: number };
  ganhas: { quantidade: number; valor: number };
  perdidas: { quantidade: number; valor: number };
  conversaoValor: number | null;
  conversaoQuantidade: number | null;
}

/**
 * D1. `noPeriodo(data)` diz se uma data (AAAA-MM-DD) cai no período escolhido; sem período, devolve sempre true.
 * Abertas usam a data de criação; ganhas e perdidas, a de encerramento.
 */
export function indicadoresOportunidades(lista: OportunidadeIndicador[], noPeriodo: (data: string | null | undefined) => boolean = () => true): IndicadoresOportunidades {
  const abertas = lista.filter((o) => estaAberta(o) && noPeriodo(o.data_criacao));
  const encerradas = lista.filter((o) => !estaAberta(o) && noPeriodo(o.data_encerramento));
  const ganhas = encerradas.filter((o) => o.estagio === 'ganha' || o.estagio === 'ganha_parcialmente');
  const perdidas = encerradas.filter((o) => o.estagio === 'perdida');
  const valorGanhas = arred(ganhas.reduce((s, o) => s + valorGanho(o), 0));
  // perdido = Perdidas + parte não ganha das parciais (encerradas no período)
  const valorPerdidas = arred(encerradas.reduce((s, o) => s + valorPerdido(o), 0));
  const denV = valorGanhas + valorPerdidas;
  const denQ = ganhas.length + perdidas.length;
  return {
    abertas: { quantidade: abertas.length, valor: arred(abertas.reduce((s, o) => s + (o.valor_vigente ?? 0), 0)) },
    ganhas: { quantidade: ganhas.length, valor: valorGanhas },
    perdidas: { quantidade: perdidas.length, valor: valorPerdidas },
    conversaoValor: denV > 0 ? valorGanhas / denV : null,
    conversaoQuantidade: denQ > 0 ? ganhas.length / denQ : null,
  };
}

export interface CabecalhoCliente {
  total: { quantidade: number; valorAbertasGanhas: number };
  abertas: { quantidade: number; valor: number };
  ganhas: { quantidade: number; valor: number };
  pedidos: { quantidade: number; valor: number };
  ticketMedio: number | null;
}

/** D2: cabeçalho do Cliente inteiro (histórico completo). */
export function cabecalhoCliente(oportunidades: OportunidadeIndicador[], pedidos: PedidoIndicador[]): CabecalhoCliente {
  const d1 = indicadoresOportunidades(oportunidades);
  const validos = pedidos.filter((p) => p.situacao !== 'Cancelado');
  const valorPedidos = arred(validos.reduce((s, p) => s + p.valores.vendido - (p.valores.saldoCancelado ?? 0), 0));
  return {
    total: { quantidade: oportunidades.length, valorAbertasGanhas: arred(d1.abertas.valor + d1.ganhas.valor) },
    abertas: d1.abertas,
    ganhas: d1.ganhas,
    pedidos: { quantidade: validos.length, valor: valorPedidos },
    ticketMedio: validos.length ? arred(valorPedidos / validos.length) : null,
  };
}
